import { useState, useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import axios from 'axios';
import { supabase } from '@/lib/supabase';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

// Product IDs — must match App Store Connect / Google Play Console EXACTLY.
// Store product identifiers are case-sensitive on both platforms: an id that
// differs only in case simply never resolves, and the paywall shows no price
// with a permanently disabled buy button.
//
// 'lifetime' is lowercase on all three stores. The legacy 'Annual' keeps its
// per-platform casing because that product already exists in App Store
// Connect that way and ids cannot be renamed after creation.
const _nativePlatform = Capacitor.getPlatform();
export const PRODUCT_IDS = {
  // The only product on sale: a one-time purchase, no renewal, no trial.
  LIFETIME: 'lifetime',
  // Retired. Still registered with the store so that existing subscribers'
  // renewal transactions continue to arrive and verify — nothing in the UI
  // offers them any more.
  MONTHLY: 'monthly',
  ANNUAL: _nativePlatform === 'ios' ? 'Annual' : 'annual',
};

// Lifetime is a non-consumable / one-time product rather than a subscription,
// which changes how it is registered with the store, how its offer is picked,
// and which Play API the backend verifies it against.
export const isLifetimeProductId = (productId) => (
  String(productId || '').toLowerCase() === 'lifetime'
);

// localStorage key used to persist purchase receipts that have NOT yet been
// confirmed by the backend (verify-iap). Receipts are dropped here the moment
// the store fires `approved` so we never lose a paid transaction even if the
// network call fails, the userId is not yet known, or the app is killed before
// verification completes. They are replayed on every app launch / login.
export const PENDING_IAP_RECEIPTS_KEY = 'betterhusband_pending_iap_receipts';

const _readPendingReceipts = () => {
  try {
    const raw = localStorage.getItem(PENDING_IAP_RECEIPTS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_e) {
    return [];
  }
};

const _writePendingReceipts = (list) => {
  try {
    localStorage.setItem(PENDING_IAP_RECEIPTS_KEY, JSON.stringify(list || []));
  } catch (_e) { /* quota / SSR — ignore */ }
};

// StoreKit / Play Billing calls can hang indefinitely — observed on iPadOS
// when the sandbox Apple ID prompt is left unanswered, and on flaky networks.
// Never `await` one bare: a stuck promise leaves `loading` pinned to true,
// which permanently disables the Subscribe and Restore buttons.
export const STORE_INIT_TIMEOUT_MS = 12000;
export const STORE_UPDATE_TIMEOUT_MS = 10000;
// Hard ceiling on the whole init sequence (initialize + update + retries).
export const IAP_LOADING_FAILSAFE_MS = 25000;

const _withTimeout = (promise, ms, label) => {
  let timeoutId;
  return Promise.race([
    Promise.resolve(promise),
    new Promise((_resolve, reject) => {
      timeoutId = setTimeout(() => reject(new Error(`${label} timed out after ${ms}ms`)), ms);
    }),
  ]).finally(() => clearTimeout(timeoutId));
};

/**
 * Initialize the CdvPurchase store exactly once per app session.
 *
 * Both IAPTransactionObserver (at app launch) and useInAppPurchase (when a
 * paywall mounts) need an initialized store. cordova-plugin-purchase must only
 * be initialized once: a second call fired while the first is still in flight
 * never resolves, which is how the subscription screen ended up loading
 * forever on slower devices — the launch-time initialize was still pending
 * when the user opened the screen. Share a single promise off the store object
 * so the first caller does the work and everyone else awaits the same result.
 *
 * `register` is invoked only for the caller that actually performs the init.
 */
export const ensureStoreInitialized = (store, storePlatform, register) => {
  if (store._iapInitPromise) return store._iapInitPromise;

  try {
    register();
  } catch (err) {
    console.warn('[IAP] Product registration failed:', err?.message || err);
  }

  const initPromise = Promise.resolve(store.initialize([storePlatform]));
  initPromise.then(
    () => { store._iapObserverReady = true; },
    // Don't cache a rejection — let a later mount retry the initialize.
    () => { store._iapInitPromise = null; },
  );
  store._iapInitPromise = initPromise;
  return initPromise;
};

/**
 * Get auth headers with token from Supabase session.
 * If token is expired, attempts refresh first.
 */
const _getAuthHeaders = async () => {
  try {
    // Attempt refresh to ensure token is fresh
    await supabase.auth.refreshSession();
    const { data } = await supabase.auth.getSession();
    const token = data?.session?.access_token;
    if (!token) {
      console.warn('[IAP] No auth token available');
      return {};
    }
    return { Authorization: `Bearer ${token}` };
  } catch (err) {
    console.warn('[IAP] Error getting auth headers:', err?.message);
    return {};
  }
};

/**
 * Post to /subscription/verify-iap with automatic token refresh on 401.
 */
const _verifyIapWithTokenRefresh = async (payload) => {
  try {
    const headers = await _getAuthHeaders();
    return await axios.post(`${API}/subscription/verify-iap`, payload, { headers });
  } catch (err) {
    // If 401 (token expired), try refreshing and retry once
    if (err.response?.status === 401) {
      console.warn('[IAP] Got 401 — attempting token refresh and retry');
      try {
        // Force a fresh token refresh
        const { data, error } = await supabase.auth.refreshSession();
        if (error || !data?.session?.access_token) {
          throw new Error(`Token refresh failed: ${error?.message || 'no token'}`);
        }
        const headers = { Authorization: `Bearer ${data.session.access_token}` };
        console.log('[IAP] Token refreshed — retrying verify-iap call');
        return await axios.post(`${API}/subscription/verify-iap`, payload, { headers });
      } catch (retryErr) {
        console.error('[IAP] Token refresh or retry failed:', retryErr?.message);
        throw err; // Throw original 401 error
      }
    }
    throw err;
  }
};

const _assertVerifyIapOk = (response) => {
  if (response?.status !== 200 || response?.data?.status !== 'ok') {
    throw new Error(`verify-iap returned non-ok status: ${response?.data?.status || 'unknown'}`);
  }
  // A 200 is only worth acting on if an entitlement actually came back.
  // Callers treat success as licence to drop the queued receipt and finish
  // the transaction, which destroys the retry channel — so an "ok" that
  // leaves the user on `free` must not be accepted as one.
  const granted = response?.data?.subscription_status;
  if (granted && granted !== 'premium' && granted !== 'trial') {
    throw new Error(`verify-iap returned ok without entitlement: ${granted}`);
  }
};

// HTTP statuses that will never succeed on a later attempt: the receipt is
// malformed, refers to a purchase the store no longer considers active, or
// belongs to a different account. Retrying these forever blocks every other
// queued receipt behind them (see replayPendingReceipts).
const _isPermanentVerifyFailure = (err) => {
  const status = err?.response?.status;
  if (typeof status !== 'number') return false;       // network / offline
  if (status === 408 || status === 429) return false; // timeout / rate limit
  return status >= 400 && status < 500;
};

const _pendingReceiptKey = (receipt) => (
  receipt?.transaction_id ||
  receipt?.original_transaction_id ||
  receipt?.receipt_data ||
  ''
);

export const getPendingReceiptKey = _pendingReceiptKey;

export const extractIapReceiptFromTransaction = (transaction, platform = Capacitor.getPlatform()) => {
  const receipt = transaction?.parentReceipt;
  const nativeData = transaction?.nativeData || {};
  const receiptNativeData = receipt?.nativeData || {};

  const iosLegacyReceipt = receiptNativeData.appStoreReceipt || '';
  const iosJws =
    nativeData.signedTransaction ||
    nativeData.jwsRepresentation ||
    receiptNativeData.signedTransaction ||
    receiptNativeData.jwsRepresentation ||
    '';
  const androidToken =
    transaction?.purchaseToken ||
    nativeData.purchaseToken ||
    receipt?.purchaseToken ||
    receiptNativeData.purchaseToken ||
    '';

  const transactionId = String(
    transaction?.transactionId ||
    nativeData.transactionId ||
    nativeData.id ||
    transaction?.id ||
    receipt?.transactionId ||
    receiptNativeData.transactionId ||
    ''
  ).trim();
  const originalTransactionId = String(
    nativeData.originalTransactionId ||
    transaction?.originalTransactionId ||
    receiptNativeData.originalTransactionId ||
    (platform === 'ios' ? transactionId : '')
  ).trim();
  const productId = transaction?.products?.[0]?.id || nativeData.productId || receipt?.products?.[0]?.id || '';

  // The transaction itself carries no amount, so read it off the store's
  // product — otherwise every IAP row lands in payment_transactions as 0.
  const pricing = productId ? window.CdvPurchase?.store?.get?.(productId)?.pricing : null;
  const priceMicros = Number(pricing?.priceMicros);

  return {
    platform,
    receipt_data: platform === 'ios' ? (iosLegacyReceipt || iosJws || '') : androidToken,
    product_id: productId,
    transaction_id: transactionId,
    original_transaction_id: originalTransactionId,
    price: Number.isFinite(priceMicros) && priceMicros >= 0 ? priceMicros / 1_000_000 : null,
    currency: pricing?.currency || '',
  };
};

export const queuePendingReceipt = (receipt) => {
  const key = _pendingReceiptKey(receipt);
  if (!receipt || !key) return;
  const list = _readPendingReceipts();
  // Dedup on transaction_id (preferred), original_transaction_id, or receipt_data.
  const filtered = list.filter((pendingReceipt) => _pendingReceiptKey(pendingReceipt) !== key);
  filtered.push({ ...receipt, queued_at: new Date().toISOString() });
  _writePendingReceipts(filtered.slice(-20));
};

export const removePendingReceipt = (key) => {
  if (!key) return;
  _writePendingReceipts(_readPendingReceipts().filter(
    (pendingReceipt) => _pendingReceiptKey(pendingReceipt) !== key
  ));
};

/**
 * Replay any queued IAP receipts against the backend. Safe to call repeatedly;
 * each successful verification removes the receipt from the queue. If a userId
 * is not provided, the call is skipped (the receipt stays queued for next time).
 */
export const replayPendingReceipts = async (userId) => {
  if (!userId) return { attempted: 0, succeeded: 0, failed: 0 };
  const list = _readPendingReceipts();
  if (!list.length) return { attempted: 0, succeeded: 0, failed: 0 };
  let succeeded = 0;
  let failed = 0;
  for (const pendingReceipt of list) {
    if (pendingReceipt.platform === 'android' && !pendingReceipt.receipt_data) {
      console.warn('[IAP] replayPendingReceipts: Android receipt has no purchaseToken; leaving queued for diagnostics');
      continue;
    }
    try {
      const verifyResponse = await _verifyIapWithTokenRefresh({
        user_id: userId,
        platform: pendingReceipt.platform,
        receipt_data: pendingReceipt.receipt_data || '',
        product_id: pendingReceipt.product_id || '',
        transaction_id: pendingReceipt.transaction_id || '',
        original_transaction_id: pendingReceipt.original_transaction_id || '',
        price: pendingReceipt.price ?? null,
        currency: pendingReceipt.currency || '',
      });
      _assertVerifyIapOk(verifyResponse);
      removePendingReceipt(_pendingReceiptKey(pendingReceipt));
      succeeded += 1;
    } catch (err) {
      // A receipt the backend will never accept (malformed, not active at the
      // store, already claimed by another account) used to sit at the head of
      // the queue and `break` every replay, on every launch, forever — taking
      // any good receipt behind it down with it. Drop those and keep going.
      if (_isPermanentVerifyFailure(err)) {
        console.warn(
          '[IAP] replayPendingReceipts: dropping permanently rejected receipt',
          err?.response?.status, err?.message || err,
        );
        removePendingReceipt(_pendingReceiptKey(pendingReceipt));
        failed += 1;
        continue;
      }
      // Transient (offline, 5xx): leave queued and stop here rather than
      // hammering the server for every remaining receipt.
      console.warn('[IAP] replayPendingReceipts: deferring', err?.message || err);
      break;
    }
  }
  return { attempted: list.length, succeeded, failed };
};

/**
 * Read the products the store has actually loaded.
 *
 * Shared by the initial load, the `productUpdated` listener and
 * refreshProducts() so the three can never disagree about which products
 * exist — refreshProducts() used to keep a second copy of this logic that had
 * never learned about the lifetime product, and calling it wiped the only
 * product on sale out of the paywall.
 */
export const readStoreProducts = (store) => {
  if (!store) return [];

  // The regular (recurring) price, not the intro/trial one: product.pricing
  // is the first pricing phase, which is $0.00 during a free trial, so the
  // real price is the last phase.
  const getRegularPricing = (product) => {
    const phases = product?.offers?.[0]?.pricingPhases;
    if (phases && phases.length > 1) {
      return phases[phases.length - 1];
    }
    return product?.pricing;
  };

  // Parse an ISO 8601 duration string into days (e.g. "P7D" -> 7, "P1W" -> 7).
  const parsePeriodDays = (period) => {
    if (!period) return 7;
    const w = period.match(/P(\d+)W/i);
    if (w) return parseInt(w[1], 10) * 7;
    const d = period.match(/P(\d+)D/i);
    if (d) return parseInt(d[1], 10);
    const m = period.match(/P(\d+)M/i);
    if (m) return parseInt(m[1], 10) * 30;
    return 7;
  };

  // Inspect a product's offers to find if a free-trial phase is available.
  // Returns { hasTrial, trialDays } so the UI can show/hide trial messaging
  // and reflect the actual eligibility reported by Google Play / App Store.
  const getTrialInfo = (product) => {
    const offers = product?.offers || [];
    for (const offer of offers) {
      const phases = (offer?.pricingPhases?.length
        ? offer.pricingPhases
        : offer?.phases) ?? [];
      for (const phase of phases) {
        const mode = String(
          phase.paymentMode ?? phase.payment_mode ?? phase.mode ?? ''
        ).toLowerCase().trim();
        const micros = Number(phase.priceMicros ?? phase.price_micros ?? -1);
        const isTrial = mode.includes('trial') || mode.includes('free') || micros === 0;
        if (isTrial) {
          const period = phase.billingPeriod ?? phase.billing_period ?? '';
          return { hasTrial: true, trialDays: parsePeriodDays(period) };
        }
      }
      // Composite offer ID fallback: "annual:annualbase:trial"
      if (String(offer?.id ?? '').toLowerCase().includes('trial')) {
        return { hasTrial: true, trialDays: 7 };
      }
    }
    return { hasTrial: false, trialDays: 0 };
  };

  const lifetime = store.get(PRODUCT_IDS.LIFETIME);
  const monthly = store.get(PRODUCT_IDS.MONTHLY);
  const yearly = store.get(PRODUCT_IDS.ANNUAL);

  const loadedProducts = [];
  if (lifetime) {
    // A non-consumable has a single price and no pricing phases, so the
    // "last phase is the real price" logic for subscriptions doesn't apply —
    // product.pricing is the price.
    const pricing = lifetime.pricing || lifetime.offers?.[0]?.pricingPhases?.[0];
    loadedProducts.push({
      id: PRODUCT_IDS.LIFETIME,
      title: lifetime.title,
      price: pricing?.price || '',
      priceMicros: pricing?.priceMicros,
      currency: pricing?.currency || '',
      period: 'lifetime',
      oneTime: true,
      hasTrial: false,
      trialDays: 0,
      raw: lifetime,
    });
  }
  if (monthly) {
    const pricing = getRegularPricing(monthly);
    const trialInfo = getTrialInfo(monthly);
    loadedProducts.push({
      id: PRODUCT_IDS.MONTHLY,
      title: monthly.title,
      price: pricing?.price || '',
      priceMicros: pricing?.priceMicros,
      currency: pricing?.currency || '',
      period: 'monthly',
      hasTrial: trialInfo.hasTrial,
      trialDays: trialInfo.trialDays,
      raw: monthly,
    });
  }
  if (yearly) {
    const pricing = getRegularPricing(yearly);
    const trialInfo = getTrialInfo(yearly);
    loadedProducts.push({
      id: PRODUCT_IDS.ANNUAL,
      title: yearly.title,
      price: pricing?.price || '',
      priceMicros: pricing?.priceMicros,
      currency: pricing?.currency || '',
      period: 'yearly',
      hasTrial: trialInfo.hasTrial,
      trialDays: trialInfo.trialDays,
      raw: yearly,
    });
  }
  return loadedProducts;
};

/**
 * Hook for managing In-App Purchases on iOS/Android via cordova-plugin-purchase.
 * Falls back to Stripe on web.
 */
export const useInAppPurchase = (userId) => {
  const [isNative, setIsNative] = useState(false);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [error, setError] = useState(null);
  const storeRef = useRef(null);
  const initializedRef = useRef(false);
  const purchaseResolverRef = useRef(null);
  const purchaseApprovalRef = useRef(null);
  // CRITICAL: handlers registered via store.when().approved(...) capture variables
  // by closure. If userId becomes available AFTER the handler is registered, the
  // closure still sees the original (often null) value. Reading from a ref keeps
  // the handler in sync with the latest userId.
  const userIdRef = useRef(userId);
  useEffect(() => {
    userIdRef.current = userId;
    if (userId && storeRef.current) {
      storeRef.current.applicationUsername = userId;
    }
  }, [userId]);

  useEffect(() => {
    const platform = Capacitor.getPlatform();
    const native = platform === 'ios' || platform === 'android';
    setIsNative(native);
    // On web there is no store to initialize, so nothing would ever clear the
    // initial `loading: true` — callers that gate UI on it would wait forever.
    if (!native) setLoading(false);
  }, []);

  // Absolute backstop for the init sequence below. Whatever happens — a
  // StoreKit call that never returns, a plugin that never fires deviceready —
  // the paywall must become interactive. Without this the Subscribe and
  // Restore buttons stay disabled for the whole session.
  useEffect(() => {
    if (!isNative) return;
    const timeoutId = setTimeout(() => {
      setLoading((prev) => {
        if (prev) console.warn('[IAP] Loading failsafe fired — releasing the paywall');
        return false;
      });
    }, IAP_LOADING_FAILSAFE_MS);
    return () => clearTimeout(timeoutId);
  }, [isNative]);

  // Initialize the store
  useEffect(() => {
    if (!isNative || initializedRef.current) return;

    const initStore = async () => {
      try {
        // cordova-plugin-purchase exposes CdvPurchase on window
        const { store, ProductType, Platform, LogLevel } = window.CdvPurchase || {};
        if (!store) {
          console.warn('[IAP] CdvPurchase not available');
          setLoading(false);
          return;
        }

        storeRef.current = store;
        initializedRef.current = true;

        store.verbosity = LogLevel?.WARNING || 1;

        const platform = Capacitor.getPlatform() === 'ios'
          ? Platform.APPLE_APPSTORE
          : Platform.GOOGLE_PLAY;

        // Set applicationUsername for StoreKit transaction association
        if (userId) {
          store.applicationUsername = userId;
        }

        // Handlers are registered BEFORE the store is initialized (below):
        // initialize() can start delivering queued transactions the moment it
        // resolves, and a purchase that lands with no `approved` handler
        // attached is a charge we would never verify.
        //
        // Always register event handlers, regardless of who initialized the store.
        // IAPTransactionObserver may have initialized first (app-launch recovery path),
        // but the approved/error handlers referencing purchaseResolverRef must be
        // registered here so purchases triggered from the paywall are handled correctly.
        // CdvPurchase store.when() is additive — registering multiple times is safe.

        // Handle approved transactions — send receipt to backend for validation.
        // CRITICAL: never throw here without first persisting the receipt to
        // localStorage. Anything that prevents transaction.finish() will cause
        // the purchase to be re-fired by the store on the next app launch, but
        // if the receipt never reaches our backend the user stays on `free`
        // even though Google/Apple charged them.
        store.when().approved(async (transaction) => {
          console.log('[IAP] Transaction approved:', transaction.transactionId);
          // Diagnostic: dump every plausible token field so we can tell at a
          // glance which path cordova-plugin-purchase populated. Google Play
          // license-testing has been observed to leave `transaction.purchaseToken`
          // undefined while exposing the token under `nativeData.purchaseToken`.
          try {
            console.log('[IAP] approved transaction shape:', JSON.stringify({
              transactionId: transaction.transactionId,
              purchaseToken: transaction.purchaseToken,
              nativeData_purchaseToken: transaction?.nativeData?.purchaseToken,
              nativeData_orderId: transaction?.nativeData?.orderId,
              parentReceipt_purchaseToken: transaction?.parentReceipt?.purchaseToken,
              parentReceipt_nativeData_purchaseToken: transaction?.parentReceipt?.nativeData?.purchaseToken,
              productIds: transaction.products?.map(p => p.id),
              state: transaction.state,
            }));
          } catch (_) { /* ignore stringify errors on circular refs */ }

          const receiptPayload = extractIapReceiptFromTransaction(transaction);
          if (receiptPayload.platform === 'android' && !receiptPayload.receipt_data) {
            console.error('[IAP] Android purchaseToken is empty across all known fields — backend will reject. Transaction id:', transaction.transactionId);
          }

          // STEP 1 — persist whatever we have locally BEFORE any network call
          // so the receipt survives a crash, offline state, or missing userId.
          // We persist even if `receiptData` is empty: the backend can still
          // reconcile via transaction_id once Apple Server Notifications fire
          // (or via /verify-iap's iOS claim path — see backend handler).
          queuePendingReceipt(receiptPayload);

          const approvalHandoff = purchaseApprovalRef.current;
          if (approvalHandoff?.onApprovalQueued && !approvalHandoff.called) {
            approvalHandoff.called = true;
            try {
              approvalHandoff.onApprovalQueued(receiptPayload);
            } catch (handoffErr) {
              console.error('[IAP] Purchase approval handoff failed:', handoffErr);
            }
          }

          // STEP 2 — read the latest userId from the ref (closure-safe).
          const currentUserId = userIdRef.current;
          if (!currentUserId) {
            console.warn('[IAP] No userId at approved-time — receipt queued for replay after login');
            // Do NOT call transaction.finish(): we want the store to re-fire
            // approved on next launch as a backup recovery channel.
            // Resolve the pending purchase as "pending verification" rather
            // than rejecting — the receipt is safely queued and will be
            // replayed. This avoids a hard error toast for the App Store
            // reviewer (Guideline 2.1(b)).
            setPurchasing(false);
            if (purchaseResolverRef.current) {
              purchaseResolverRef.current.reject(
                new Error('Purchase verification deferred')
              );
              purchaseResolverRef.current = null;
            }
            purchaseApprovalRef.current = null;
            return;
          }

          try {
            const verifyResponse = await _verifyIapWithTokenRefresh({
              user_id: currentUserId,
              platform: receiptPayload.platform,
              receipt_data: receiptPayload.receipt_data,
              product_id: receiptPayload.product_id,
              transaction_id: receiptPayload.transaction_id,
              original_transaction_id: receiptPayload.original_transaction_id,
              price: receiptPayload.price,
              currency: receiptPayload.currency,
            });
            _assertVerifyIapOk(verifyResponse);
            removePendingReceipt(_pendingReceiptKey(receiptPayload));
            await transaction.finish();
            console.log('[IAP] Transaction finished and verified');
            if (purchaseResolverRef.current) {
              purchaseResolverRef.current.resolve(true);
              purchaseResolverRef.current = null;
            }
            purchaseApprovalRef.current = null;
          } catch (err) {
            console.error('[IAP] Receipt verification failed (will retry on next launch):', err?.message);
            setError('Verification failed. We will retry automatically.');
            if (purchaseResolverRef.current) {
              purchaseResolverRef.current.reject(err);
              purchaseResolverRef.current = null;
            }
            purchaseApprovalRef.current = null;
          } finally {
            setPurchasing(false);
          }
        });

        store.when().finished((transaction) => {
          console.log('[IAP] Transaction finished:', transaction.transactionId);
          setPurchasing(false);
        });

        // Handle errors (including user cancellation)
        // err.code is numeric; ErrorCode.PAYMENT_CANCELLED = 6
        store.error((err) => {
          console.error('[IAP] Store error:', err);
          const isCancelled = err.code === 6 ||
            (typeof err.message === 'string' && err.message.toLowerCase().includes('cancel'));
          if (!isCancelled) {
            setError(err.message || 'Purchase failed');
          }
          // Reject the pending purchase promise so the caller isn't stuck waiting
          if (purchaseResolverRef.current) {
            purchaseResolverRef.current.reject(err);
            purchaseResolverRef.current = null;
          }
          setPurchasing(false);
        });

        // Initialize through the shared helper so we never fire a second
        // concurrent initialize() against a store the launch-time observer is
        // still bringing up. Timed out rather than awaited bare: a hung
        // StoreKit call must not hold the paywall hostage.
        try {
          await _withTimeout(
            ensureStoreInitialized(store, platform, () => {
              store.register([
                // On sale: one-time purchase.
                { id: PRODUCT_IDS.LIFETIME, type: ProductType.NON_CONSUMABLE, platform },
                // Retired, but kept registered so renewals from existing
                // subscribers still arrive as `approved` transactions and
                // reach verify-iap.
                { id: PRODUCT_IDS.MONTHLY, type: ProductType.PAID_SUBSCRIPTION, platform },
                { id: PRODUCT_IDS.ANNUAL, type: ProductType.PAID_SUBSCRIPTION, platform },
              ]);
            }),
            STORE_INIT_TIMEOUT_MS,
            'store.initialize',
          );
        } catch (initErr) {
          // Non-fatal: fall through to the product read + retries below. The
          // store often catches up on its own, and even if it doesn't the UI
          // has to come out of its loading state.
          console.warn('[IAP] Store initialize did not complete:', initErr?.message || initErr);
        }

        // Wrapped for the same reason as initialize — store.update() talks to
        // the App Store / Play Billing and can stall indefinitely.
        const safeStoreUpdate = async () => {
          try {
            await _withTimeout(store.update(), STORE_UPDATE_TIMEOUT_MS, 'store.update');
          } catch (updateErr) {
            console.warn('[IAP] store.update() failed:', updateErr?.message || updateErr);
          }
        };

        await safeStoreUpdate();

        const readProducts = () => readStoreProducts(store);

        let loadedProducts = readProducts();

        // Retry once after 2s if no products loaded (sandbox/network delay)
        if (loadedProducts.length === 0) {
          console.warn('[IAP] No products loaded on first attempt, retrying in 2s...');
          await new Promise(r => setTimeout(r, 2000));
          await safeStoreUpdate();
          loadedProducts = readProducts();
          if (loadedProducts.length === 0) {
            console.warn('[IAP] No products after 1st retry, retrying in 3s...');
            await new Promise(r => setTimeout(r, 3000));
            await safeStoreUpdate();
            loadedProducts = readProducts();
          }
          if (loadedProducts.length === 0) {
            console.warn('[IAP] No products loaded after retries. Check App Store Connect product IDs:', PRODUCT_IDS);
          }
        }

        setProducts(loadedProducts);
        console.log('[IAP] Products loaded:', loadedProducts.length);

        // Refresh product list whenever Google Play sends updated product details.
        // Offers (including trial data) can arrive after store.update() resolves.
        // Registered here so readProducts() is guaranteed to be in scope.
        store.when().productUpdated((product) => {
          console.log('[IAP] Product updated:', product.id);
          setProducts(readProducts());
        });

        // Silent restore on Android: re-validates all owned subscriptions against
        // the Play API. This re-fires `approved` → `verify-iap` for any active
        // purchase, correcting subscription_status without user interaction.
        // iOS is intentionally excluded because restorePurchases shows an Apple
        // sign-in dialog on that platform.
        if (Capacitor.getPlatform() === 'android') {
          try {
            console.log('[IAP] Triggering silent restore to sync subscription status...');
            await _withTimeout(store.restorePurchases(), STORE_UPDATE_TIMEOUT_MS, 'store.restorePurchases');
            console.log('[IAP] Silent restore complete');
          } catch (restoreErr) {
            // Non-blocking — don't surface this to the user
            console.warn('[IAP] Silent restore error (non-fatal):', restoreErr);
          }
        }
      } catch (err) {
        console.error('[IAP] Init error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    // Wait for deviceready to ensure cordova plugins are loaded
    if (window.CdvPurchase) {
      initStore();
    } else {
      document.addEventListener('deviceready', initStore, { once: true });
      // Fallback: if deviceready already fired, try after short delay
      setTimeout(() => {
        if (!initializedRef.current && window.CdvPurchase) initStore();
        else if (!initializedRef.current) setLoading(false);
      }, 3000);
    }
  }, [isNative, userId]);

  // Purchase a product
  const purchase = useCallback(async (productId, options = {}) => {
    const store = storeRef.current;
    if (!store) {
      setError('Store not initialized. Please restart the app.');
      return false;
    }

    const checkoutUserId = options?.userId || options?.user_id || '';
    if (checkoutUserId) {
      userIdRef.current = checkoutUserId;
      store.applicationUsername = checkoutUserId;
      try {
        localStorage.setItem('betterhusbandUserId', checkoutUserId);
      } catch (_err) { /* storage unavailable — non-fatal */ }
    }

    setPurchasing(true);
    setError(null);

    // Signal to IAPTransactionObserver that we're handling this purchase
    if (store) store._iapPurchaseInProgress = true;
    let verificationTimeout = null;

    try {
      const product = store.get(productId);
      if (!product) {
        console.error('[IAP] Product not found in store:', productId, 'Available:', store.products?.map(p => p.id));
        // Try refreshing products once (timed out — the user is waiting on a
        // button press here, so a stalled store must not hang the purchase).
        try {
          await _withTimeout(store.update(), STORE_UPDATE_TIMEOUT_MS, 'store.update');
        } catch (updateErr) {
          console.warn('[IAP] store.update() before purchase failed:', updateErr?.message || updateErr);
        }
        const retryProduct = store.get(productId);
        if (!retryProduct) {
          // Diagnostic: dump which products *did* load. If e.g. 'annual' loaded
          // but 'monthly' didn't, it almost always indicates the monthly
          // base plan isn't active/published in Google Play Console, or the
          // signed APK isn't uploaded under the same package the product is
          // attached to.
          const loadedIds = (store.products || []).map(p => p.id).join(', ') || 'none';
          console.error(`[IAP] Product "${productId}" missing. Loaded products: [${loadedIds}]`);
          setError(
            `This purchase isn\u2019t available right now ` +
            `("${productId}" not loaded from the store; ` +
            `loaded: ${loadedIds}). ` +
            `Please try again in a few minutes or restart the app.`
          );
          setPurchasing(false);
          return false;
        }
      }

      const resolvedProduct = product || store.get(productId);

      // ── One-time product (lifetime) ─────────────────────────────────────────
      // A non-consumable has exactly one offer and no pricing phases, so none
      // of the trial / base-plan / composite-offer-id selection below applies.
      // Running it anyway would pick nothing on Play (no offer matches
      // "<id>:<basePlan>:trial") and fail a purchase that is actually fine.
      if (isLifetimeProductId(productId)) {
        const lifetimeOffer = resolvedProduct?.getOffer?.() || resolvedProduct?.offers?.[0];
        if (!lifetimeOffer) {
          console.error('[IAP] No offer on lifetime product:', productId, JSON.stringify(resolvedProduct?.offers || []));
          setError('This purchase isn’t available right now. Please try again in a few minutes.');
          setPurchasing(false);
          return false;
        }
        console.log('[IAP] Lifetime offer selected:', lifetimeOffer.id);

        purchaseApprovalRef.current = {
          onApprovalQueued: typeof options?.onApprovalQueued === 'function'
            ? options.onApprovalQueued
            : null,
          called: false,
        };

        const lifetimeVerification = new Promise((resolve, reject) => {
          purchaseResolverRef.current = { resolve, reject };
          verificationTimeout = setTimeout(() => {
            if (purchaseResolverRef.current) {
              purchaseResolverRef.current = null;
              reject(new Error('Purchase verification timed out'));
            }
          }, 30000);
        });

        const lifetimeResult = await store.order(lifetimeOffer);
        if (lifetimeResult?.isError) {
          if (verificationTimeout) clearTimeout(verificationTimeout);
          purchaseResolverRef.current = null;
          purchaseApprovalRef.current = null;
          if (lifetimeResult.code !== 'CdvPurchase.PAYMENT_CANCELLED') {
            const rawMsg = String(lifetimeResult.message || '').toLowerCase();
            console.error('[IAP] store.order() rejected for lifetime:', {
              productId, offerId: lifetimeOffer?.id,
              code: lifetimeResult.code, message: lifetimeResult.message,
            });
            setError(
              rawMsg.includes('already owned') || rawMsg.includes('already_owned')
                ? 'You already own this. Use "Restore Purchases" to unlock it on this device.'
                : (lifetimeResult.message || 'Purchase failed')
            );
          }
          setPurchasing(false);
          return false;
        }

        try {
          return await lifetimeVerification;
        } finally {
          if (verificationTimeout) clearTimeout(verificationTimeout);
          purchaseApprovalRef.current = null;
        }
      }

      // ── Full offer dump for diagnostics ─────────────────────────────────────
      const allOffers = resolvedProduct?.offers || [];
      console.log('[IAP] All offers raw:', JSON.stringify(allOffers));
      console.log('[IAP] Product offers summary:', JSON.stringify(
        allOffers.map(o => ({
          id: o.id,
          basePlanId: o.basePlanId ?? o.base_plan_id ?? o.googlePlayBasePlanId,
          offerId: o.offerId ?? o.offer_id ?? o.googlePlayOfferId,
          phases: (o.pricingPhases || o.phases || []).map(p => ({
            mode: p.paymentMode ?? p.payment_mode,
            micros: p.priceMicros ?? p.price_micros,
            period: p.billingPeriod ?? p.billing_period,
          })),
        }))
      ));

      // ── Helpers ──────────────────────────────────────────────────────────────
      const normalize = (value) => String(value ?? '').toLowerCase().trim();

      // Returns true if the offer has any free/trial pricing phase.
      // Fix: if pricingPhases exists but is empty [], it would block the ?? fallback
      // to phases — so we check length explicitly before falling back.
      const hasTrialPhase = (candidateOffer) => {
        const phases = (candidateOffer?.pricingPhases?.length
          ? candidateOffer.pricingPhases
          : candidateOffer?.phases) ?? [];
        return phases.some(phase => {
          const mode = normalize(
            phase.paymentMode ?? phase.payment_mode ?? phase.mode ?? ''
          );
          const micros = Number(phase.priceMicros ?? phase.price_micros ?? -1);
          // Google Play free trial: 0 micros OR mode contains 'trial' OR 'free'
          return mode.includes('trial') || mode.includes('free') || micros === 0;
        });
      };

      // Returns true if the offer's serialized form contains the keyword 'trial'
      // (catches composite IDs like "monthly:monthlybase:trial").
      const hasTrial = (candidateOffer) =>
        hasTrialPhase(candidateOffer) ||
        normalize(candidateOffer?.id ?? '').includes('trial') ||
        normalize(candidateOffer?.offerId ?? '').includes('trial') ||
        normalize(candidateOffer?.offer_id ?? '').includes('trial');

      // ── Diagnostic: dump phases per offer ───────────────────────────────────
      allOffers.forEach((o, i) => {
        const phases = (o?.pricingPhases?.length ? o.pricingPhases : o?.phases) ?? [];
        console.log(`[IAP] Offer ${i} (${o.id}) phases:`, JSON.stringify(phases));
      });

      // ── Offer selection ──────────────────────────────────────────────────────
      // Apple App Store: a single subscription has one introductory free-trial
      // offer attached to the base product. CdvPurchase auto-selects it via
      // product.getOffer(); the Google Play composite-ID lookup below is a no-op
      // on iOS and was generating misleading "no offer found" logs during
      // App Store review. Skip it on iOS entirely.
      const isIOS = Capacitor.getPlatform() === 'ios';

      let offer;
      let exactTrialOffer;
      let trialOffer;
      let autoOffer;
      let autoHasTrial;

      if (isIOS) {
        autoOffer = resolvedProduct?.getOffer?.();
        autoHasTrial = hasTrial(autoOffer);
        // On iOS prefer the auto-selected offer; fall back to any trial-bearing
        // offer if Apple ever exposes multiple.
        offer = autoOffer || allOffers.find(hasTrial) || allOffers[0];
        console.log('[IAP][iOS] Selected offer:', offer?.id, '| autoHasTrial:', autoHasTrial);
      } else {
        // Google Play: build the expected composite trial offer ID from the
        // registered product ID. Format: <productId>:<basePlanId>:<offerId>
        // For monthly: "monthly:monthlybase:trial"
        // For annual: "annual:annualbase:trial"
        const BASE_PLAN_MAP = {
          [PRODUCT_IDS.MONTHLY]: 'monthlybase',
          [PRODUCT_IDS.ANNUAL]: 'annualbase',
        };
        const basePlanId = BASE_PLAN_MAP[productId] ?? allOffers[0]?.basePlanId ?? 'base';
        const TRIAL_OFFER_ID = `${productId}:${basePlanId}:trial`;
        console.log('[IAP] TRIAL_OFFER_ID:', TRIAL_OFFER_ID, '| basePlanId:', basePlanId);

        // Helpers for Play eligibility. Some users are not eligible for trial
        // offers even if those offers exist, and forcing them can trigger
        // generic Play purchase errors.
        const canBuy = (candidateOffer) =>
          !!candidateOffer && candidateOffer.canPurchase !== false;
        const firstBuyable = (candidates) => candidates.find(canBuy);

        // Candidate offers.
        exactTrialOffer = resolvedProduct?.getOffer?.(TRIAL_OFFER_ID);
        trialOffer = exactTrialOffer || allOffers.find(hasTrial);
        autoOffer = resolvedProduct?.getOffer?.();
        autoHasTrial = hasTrial(autoOffer);

        const nonTrialOffers = allOffers.filter(o => !hasTrial(o));
        const trialOffers = allOffers.filter(hasTrial);

        // Selection order:
        // 1) Store-selected offer if purchasable (usually best/eligible)
        // 2) Any non-trial purchasable offer (safe fallback)
        // 3) Trial purchasable offer
        // 4) Auto offer even if eligibility is unknown
        // 5) First purchasable offer from all
        offer =
          (canBuy(autoOffer) ? autoOffer : null)
          || firstBuyable(nonTrialOffers)
          || firstBuyable([trialOffer, ...trialOffers])
          || autoOffer
          || firstBuyable(allOffers);

        console.log('[IAP] Offers available:', allOffers.map(o => o.id));
        console.log(
          '[IAP] Selected offer:', offer?.id,
          '| exactTrialOffer:', exactTrialOffer?.id,
          '| trialOffer:', trialOffer?.id,
          '| autoOffer:', autoOffer?.id, '| autoHasTrial:', autoHasTrial,
        );
      }
      if (!offer) {
        console.error('[IAP] No offer for product:', productId);
        setError('No subscription offer available. Please try again.');
        setPurchasing(false);
        return false;
      }

      purchaseApprovalRef.current = {
        onApprovalQueued: typeof options?.onApprovalQueued === 'function'
          ? options.onApprovalQueued
          : null,
        called: false,
      };

      const verificationPromise = new Promise((resolve, reject) => {
        purchaseResolverRef.current = { resolve, reject };
        verificationTimeout = setTimeout(() => {
          if (purchaseResolverRef.current) {
            purchaseResolverRef.current = null;
            reject(new Error('Purchase verification timed out'));
          }
        }, 30000);
      });

      const result = await store.order(offer);
      if (result?.isError) {
        if (verificationTimeout) clearTimeout(verificationTimeout);
        purchaseResolverRef.current = null;
        purchaseApprovalRef.current = null;
        if (result.code !== 'CdvPurchase.PAYMENT_CANCELLED') {
          // Surface the raw Google Play / StoreKit code so users (and the
          // logs) see what actually happened rather than a generic
          // "Purchase failed". Most common Google Play causes:
          //   ITEM_ALREADY_OWNED -> user already has another active sub
          //                        in the same subscription group
          //   ITEM_UNAVAILABLE   -> base plan not active in Play Console
          //   DEVELOPER_ERROR    -> productId / offerToken mismatch
          const rawCode = result.code || 'unknown';
          const rawMsg = result.message || '';
          console.error('[IAP] store.order() rejected:', {
            productId, offerId: offer?.id, code: rawCode, message: rawMsg,
          });
          const friendly = (() => {
            const m = String(rawMsg).toLowerCase();
            if (m.includes('already owned') || m.includes('already_owned')) {
              // Lifetime is a non-consumable, so this is now mostly the
              // reinstall case: the store still has the purchase and simply
              // won't sell it twice. Restore re-delivers it to the `approved`
              // handler, which re-verifies and re-grants. Pointing these users
              // at Play's subscription management (as the old copy did) sent
              // them somewhere that shows nothing at all.
              return isLifetimeProductId(productId)
                ? 'You already own this. Tap "Restore Purchases" to get your access back.'
                : 'You already have an active subscription. Use "Restore Purchases" or manage it from Google Play.';
            }
            if (m.includes('unavailable') || m.includes('item_unavailable')) {
              return 'This subscription plan is temporarily unavailable from Google Play. Please try again in a few minutes.';
            }
            if (m.includes('developer_error') || m.includes('developer error')) {
              return 'Subscription configuration error. Please contact support.';
            }
            return rawMsg || 'Purchase failed';
          })();
          setError(friendly);
        }
        setPurchasing(false);
        return false;
      }

      // Wait for the approved handler to verify the receipt with the backend
      try {
        return await verificationPromise;
      } finally {
        if (verificationTimeout) clearTimeout(verificationTimeout);
        purchaseApprovalRef.current = null;
      }
    } catch (err) {
      console.error('[IAP] Purchase error:', err);
      if (verificationTimeout) clearTimeout(verificationTimeout);
      purchaseResolverRef.current = null;
      purchaseApprovalRef.current = null;
      // Don't show error for user cancellation (code 6 = PAYMENT_CANCELLED)
      const isCancelled = err?.code === 6 ||
        (typeof err?.message === 'string' && err.message.toLowerCase().includes('cancel'));
      if (!isCancelled && err?.message !== 'Purchase verification timed out') {
        setError(err.message || 'Purchase failed');
      }
      setPurchasing(false);
      return false;
    } finally {
      if (store) store._iapPurchaseInProgress = false;
    }
  }, []);

  // Restore previous purchases
  const restorePurchases = useCallback(async () => {
    const store = storeRef.current;
    if (!store) return;

    setLoading(true);
    try {
      if (userIdRef.current) {
        store.applicationUsername = userIdRef.current;
      }
      // Timed out: on iOS this shows an Apple ID dialog, and a dismissed or
      // stalled dialog would otherwise leave the button spinning forever.
      await _withTimeout(store.restorePurchases(), STORE_UPDATE_TIMEOUT_MS, 'store.restorePurchases');
    } catch (err) {
      console.error('[IAP] Restore error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Refresh products (retry loading from App Store)
  const refreshProducts = useCallback(async () => {
    const store = storeRef.current;
    if (!store) return [];

    setLoading(true);
    setError(null);
    try {
      await _withTimeout(store.update(), STORE_UPDATE_TIMEOUT_MS, 'store.update');

      const loadedProducts = readStoreProducts(store);
      setProducts(loadedProducts);
      console.log('[IAP] refreshProducts:', loadedProducts.length, 'products');
      return loadedProducts;
    } catch (err) {
      console.error('[IAP] refreshProducts error:', err);
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Whenever userId becomes available (or changes), replay any IAP receipts
  // queued from earlier sessions. This is the primary recovery channel for
  // purchases approved before the user was authenticated, network failures,
  // or app crashes between approval and verification.
  useEffect(() => {
    if (!userId) return;
    replayPendingReceipts(userId).then((r) => {
      if (r.attempted > 0) {
        console.log(`[IAP] Replayed pending receipts: ${r.succeeded}/${r.attempted}`);
      }
    });
  }, [userId]);

  return {
    isNative,
    products,
    loading,
    purchasing,
    error,
    purchase,
    restorePurchases,
    refreshProducts,
    PRODUCT_IDS,
  };
};

export default useInAppPurchase;
